import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import Select from "react-select";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { DataTableCenteredLoader } from "components/DataTableLoader";

import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';


import { getAuthCookie } from "../utils/authCookies";

interface InstallationOrderRow {
    order_id: string;
    agent_name: string;
    ordered_by: string;
    brokerage_firm: string;
    office: string;
    install_date: string;
    job_type: string;
    status: number | string;
    amount: string;
    payment_status: string;
    payment_method: string;
    notes?: string;
}

const DATE_OPTIONS = [
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
];

const PAYMENT_STATUS_OPTIONS = [
    { label: "Paid", value: "Paid" },
    { label: "Unpaid", value: "Pending" },
];

const PAYMENT_METHOD_OPTIONS = [
    { label: "Card", value: "Card" },
    { label: "Net 30", value: "Net 30" },
    { label: "Bank Transfer", value: "Bank Transfer" },
];

export default function InstallationOrderAmdBillingReport() {
    const userId = getAuthCookie("userId");
    const token = getAuthCookie("sessionToken");
    const [data, setData] = useState<InstallationOrderRow[]>([]);
    const [datePreset, setDatePreset] = useState(DATE_OPTIONS[2]); // default: Last 30 Days
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [paymentStatus, setPaymentStatus] = useState<any[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<any[]>([]);
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");

    const navigate = useNavigate();
    const location = useLocation();


    async function fetchData() {
        setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/reports/signage/installation_orders/datatable`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: pagination.pageSize,
                    page: pagination.pageIndex + 1,
                    date_range: {
                        type: datePreset.value,
                        from: datePreset.value === "custom" ? customFrom : "",
                        to: datePreset.value === "custom" ? customTo : "",
                    },
                    payment_status: paymentStatus.map((x: any) => x.value),
                    search: globalFilter,
                }),
            });
            const result = await res.json();
            console.log("Installation Orders Data:", result.data);
            setData(result.data || []);
            setRowCount(result.total || 0);
        } catch (err) {
            console.error("Error fetching installation orders:", err);
            setData([]);
            setRowCount(0);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [globalFilter, pagination]);

    const columnHelper = createColumnHelper<InstallationOrderRow>();

    // Define columns (memoized for TanStack)
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: "sno",
                header: () => <div className="table-headers text-center">S.No.</div>,
                cell: (info) => (
                    <div className="table-cells text-center">
                        {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
                    </div>
                ),
            }),
            columnHelper.accessor("order_id", {
                header: () => <div className="table-headers text-center">Order ID</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("agent_name", {
                header: () => <div className="table-headers text-center">Agent Name</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("ordered_by", {
                header: () => <div className="table-headers text-center">Ordered By</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("brokerage_firm", {
                header: () => <div className="table-headers text-center">Brokerage Firm</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("office", {
                header: () => <div className="table-headers text-center">Office</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("install_date", {
                header: () => <div className="table-headers text-center">Install Date</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("job_type", {
                header: () => <div className="table-headers text-center">Job Type</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("status", {
                header: () => <div className="table-headers text-center">Status</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("amount", {
                header: () => <div className="table-headers text-center">Amount</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("payment_status", {
                header: () => <div className="table-headers text-center">Payment Status</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
            columnHelper.accessor("payment_method", {
                header: () => <div className="table-headers text-center">Payment Method</div>,
                cell: (info) => (
                    <div className="table-cells text-center">{info.getValue() || "--"}</div>
                ),
            }),
        ],
        [pagination]
    );


    // TanStack table instance
    const table = useReactTable({
        data: data || [],
        columns,
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        state: {
            pagination,
        },
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
    });

    return (
        <div className="content">
            <div className="d-flex  justify-content-between align-items-center border-bottom pb-3 mb-4 page-container">
                <h4 className="mb-0 fw-bold">Installation Orders & Billing Reports </h4>

                <div className="d-flex gap-3 align-items-center">
                    {/* Search Bar */}
                    <div className="input-group" style={{ width: "230px", height: "40px" }}>
                        <span className="input-group-text bg-white border-end-0 p-2 left-radius">
                            <i className="ri-search-line fs-15"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0 ps-0 right-radius"
                            placeholder="Search by Name and Email"
                            value={searchInput}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === "Enter") {
                                    setGlobalFilter(searchInput);
                                }
                            }}
                            style={{ borderColor: "#cccccc" }}
                        />
                    </div>
                </div>
            </div>
            {/* <h4 className="mb-0 fw-bold">Installation Orders & Billing Report</h4> */}
            <div className="row mb-3 align-items-end">
                {/* Date Range */}
                <div className="col" style={{ flex: "0 0 12.5%", maxWidth: "12.5%" }}>
                    <label className="form-label table-cells">Date Range</label>
                    <Select
                        options={DATE_OPTIONS}
                        value={datePreset}
                        onChange={val => setDatePreset(val!)}

                    />
                </div>
                {datePreset.value === "custom" && (
                    <>
                        <div className="col-md-2">
                            <label className="form-label table-cells">From</label>
                            <input
                                type="date"
                                className="form-control"
                                value={customFrom}
                                onChange={e => setCustomFrom(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label table-cells">To</label>
                            <input
                                type="date"
                                className="form-control"
                                value={customTo}
                                onChange={e => setCustomTo(e.target.value)}
                            />
                        </div>
                    </>
                )}
                {/* Payment Status */}
                <div className="col" style={{ flex: "0 0 12.5%", maxWidth: "12.5%" }}>
                    <label className="form-label table-cells">Payment Status</label>
                    <Select
                        isMulti
                        options={PAYMENT_STATUS_OPTIONS}
                        value={paymentStatus}
                        onChange={val => setPaymentStatus(Array.from(val as any[]))}

                    />
                </div>
                {/* Payment Method */}
                <div className="col" style={{ flex: "0 0 12.5%", maxWidth: "12.5%" }}>
                    <label className="form-label table-cells">Payment Method</label>
                    <Select
                        isMulti
                        options={PAYMENT_METHOD_OPTIONS}
                        value={paymentMethod}
                        onChange={val => setPaymentMethod(Array.from(val as any[]))}

                    />
                </div>
                {/* Search Bar */}
                {/* <div className="col-md-3">
                    <label className="form-label table-cells">Search</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search agent, office, brokerage, team..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") fetchData(); }}
                    />
                </div> */}
                <div className="col-md-2">
                    <button
                        className="btn btn-primary  mt-4"
                        onClick={(e) => {
                            e.preventDefault(); // prevent form reload
                            fetchData(); // ✅ only calls when user clicks
                        }}
                    >
                        Apply Filter
                    </button>
                </div>

            </div>
            {/* Table */}
            {loading ? (
                <DataTableCenteredLoader message="Loading installation orders…" />
            ) : (
                <DataTable table={table} />
            )}
            {/* Pagination */}
            {loading ? null : table.getRowModel().rows.length === 0 ? (
                <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                    No records found
                </div>
            ) : (
                <div className="border-bottom pb-3 mb-4 page-container d-block">
                    <PaginationControls table={table} />
                </div>
            )}
        </div>
    )
}